import { Avatar, Box } from "@chakra-ui/react"
import useImageStatus from "@pathwright/utils/src/img/useImageStatus"
import Pathicon from "@pathwright/features/src/modules/pathicon/Pathicon"
import { forwardRef, type ReactNode } from "react"
import { AvatarUser } from "./types"
import { getAvatarFallbackBG, getImage } from "./utils"

export interface AvatarProps extends Avatar.RootProps {
  srcSet?: string
  icon?: React.ReactElement
  fallback?: React.ReactNode
}

export type UserAvatarProps = {
  user: AvatarUser
  preferIcon?: boolean
  children?: ReactNode
} & Omit<AvatarProps, "children">

function AvatarImage({ image }: { image?: string }) {
  const imgStatus = useImageStatus(image)

  // Show transparent placeholder while loading to avoid flash of fallback avatar
  // which is always shown when image is not yet loaded.
  return imgStatus === "idle" ? (
    <Avatar.Image bg="transparent" />
  ) : (
    <Avatar.Image src={image} />
  )
}

// Dumb component that accepts the group.
export const UserAvatar = forwardRef<HTMLDivElement, UserAvatarProps>(
  ({ user, preferIcon, icon, fallback, children, ...avatarProps }, ref) => {
    // Get standard fallback avatar color.
    function getBgColor(user: AvatarUser) {
      const color = getAvatarFallbackBG(user)
      // Don't provide bg color when image is available.
      return getImage(user) ? undefined : color
    }

    const image = getImage(user)
    const bg = getBgColor(user)
    // Use icon when preferred, when there is no image.
    const useIcon = preferIcon && !image

    return useIcon ? (
      <Avatar.Root ref={ref} bg="transparent" border="none" {...avatarProps}>
        <Avatar.Fallback
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box bg={bg} />
          {fallback || icon || <Pathicon icon="avatar-fill" size="60%" />}
        </Avatar.Fallback>
        {children as ReactNode}
      </Avatar.Root>
    ) : (
      <Avatar.Root ref={ref} bg="transparent" border="none" {...avatarProps}>
        <Avatar.Fallback
          name={user.full_name!}
          bg={bg}
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="white"
        >
          {fallback}
        </Avatar.Fallback>
        <AvatarImage image={image} />
        {children as ReactNode}
      </Avatar.Root>
    )
  }
)

export default UserAvatar
